/**
 * Persistent Audio Element
 *
 * CRITICAL COMPONENT — This is the SINGLE <audio> element for the entire app.
 *
 * Architecture:
 *   AudioPlayerContext (state) ←→ PersistentAudioElement (<audio>) ←→ R2 streaming
 *                                       ↕
 *                              EnhancedAudioPlayer (fixed bottom bar)
 *                              TrackPlayer (per-row play buttons)
 *                              SongDetailsWidget (sidebar widget)
 *
 * Why this exists:
 *   - HTMLAudioElements are heavy; creating a new one per page destroys playback
 *   - The <audio> element must survive page navigation (React Router route changes)
 *   - All player UI components read/write through AudioPlayerContext
 *   - This component is the bridge: context state changes → audio element actions
 *     and audio element events → context state updates
 *
 * Data flow:
 *   ┌──────────────┐  setCurrentTrack/play/pause/seekTo  ┌──────────────────┐
 *   │   UI Layer   │─────────────────────────────────────►│  AudioPlayer     │
 *   │  (Enhanced-  │                                      │  Context         │
 *   │   AudioPlayer│◄─────────────────────────────────────│  (state mgmt)    │
 *   │   TrackPlayer│     state.currentTime / isPlaying    └────────┬─────────┘
 *   │   etc.)      │                                              │
 *   └──────────────┘                                   useEffect │ sync
 *                                                              │
 *                                                       ┌──────▼──────────┐
 *                                                       │  Persistent     │
 *                                                       │  AudioElement   │
 *                                                       │  (<audio> ref)  │
 *                                                       └──────┬──────────┘
 *                                                              │
 *                                                       ┌──────▼──────────┐
 *                                                       │  Cloudflare R2  │
 *                                                       │  (streaming via  │
 *                                                       │   signed JWT)    │
 *                                                       └─────────────────┘
 */

import { useEffect, useRef, useCallback } from 'react';
import { useAudioPlayer } from '@/context/AudioPlayerContext';
import { auth } from '@/lib/auth';

export function PersistentAudioElement() {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const canPlayHandlerRef = useRef<(() => void) | null>(null);
  const {
    state,
    setAudioElement,
    setDuration,
    setCurrentTime,
    setBuffered,
    setLoading,
    setError,
    trackEnded,
  } = useAudioPlayer();

  // ──────────────────────────────────────────────
  // 1. Create and register the <audio> element
  // ──────────────────────────────────────────────
  useEffect(() => {
    const audio = new Audio();
    audio.preload = 'metadata';
    setAudioElement(audio);
    audioRef.current = audio;

    return () => {
      // Cleanup: remove event listeners, stop playback, release the element
      audio.pause();
      audio.src = '';
      audio.load(); // forces release of media resource
      setAudioElement(null);
      audioRef.current = null;
    };
  }, [setAudioElement]);

  // ──────────────────────────────────────────────
  // 2. Sync audio source when track changes
  // ──────────────────────────────────────────────
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !state.currentTrack?.url) return;

    // Append auth token — browser <audio> elements can't set custom headers,
    // so the token must be passed as a query parameter.
    const token = auth.getToken();
    let newUrl = state.currentTrack.url;
    if (token) {
      const separator = newUrl.includes('?') ? '&' : '?';
      newUrl = `${newUrl}${separator}token=${encodeURIComponent(token)}`;
    }

    if (audio.src !== newUrl) {
      audio.src = newUrl;
      audio.load();
      setLoading(true);
    }
  }, [state.currentTrack?.url, setLoading]);

  // ──────────────────────────────────────────────
  // 3. Sync play/pause with state
  // ──────────────────────────────────────────────
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !state.currentTrack?.url) return;

    if (state.isPlaying && audio.paused) {
      // If audio hasn't loaded yet, wait for canplay
      if (audio.readyState < 3) {
        // Clean up any previous canplay handler
        if (canPlayHandlerRef.current) {
          audio.removeEventListener('canplay', canPlayHandlerRef.current);
        }

        const playWhenReady = () => {
          audio.play().catch((err) => {
            const message =
              err.name === 'NotAllowedError'
                ? 'Click play to start audio'
                : `Playback failed: ${err.message}`;
            setError(message);
            setLoading(false);
          });
          audio.removeEventListener('canplay', playWhenReady);
          canPlayHandlerRef.current = null;
        };

        canPlayHandlerRef.current = playWhenReady;
        audio.addEventListener('canplay', playWhenReady);

        // Timeout safety: if audio takes > 15s to load, give up
        const timeout = setTimeout(() => {
          if (canPlayHandlerRef.current) {
            audio.removeEventListener('canplay', canPlayHandlerRef.current);
            canPlayHandlerRef.current = null;
            setError('Audio loading timed out. Check your connection or try again.');
            setLoading(false);
          }
        }, 15000);

        // Clear timeout if canplay fires
        audio.addEventListener(
          'canplay',
          () => clearTimeout(timeout),
          { once: true }
        );
      } else {
        // Audio already loaded, play immediately
        audio.play().catch((err) => {
          setError(`Playback failed: ${err.message}`);
          setLoading(false);
        });
      }
    } else if (!state.isPlaying && !audio.paused) {
      audio.pause();
    }
  }, [state.isPlaying, state.currentTrack?.url, setError, setLoading]);

  // ──────────────────────────────────────────────
  // 4. Sync volume/mute
  // ──────────────────────────────────────────────
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.volume = state.isMuted ? 0 : state.volume;
  }, [state.volume, state.isMuted]);

  // ──────────────────────────────────────────────
  // 5. Sync playback rate
  // ──────────────────────────────────────────────
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.playbackRate = state.playbackRate;
  }, [state.playbackRate]);

  // ──────────────────────────────────────────────
  // 6. Attach audio event listeners → context
  // ──────────────────────────────────────────────
  const handleTimeUpdate = useCallback(() => {
    const audio = audioRef.current;
    if (audio) setCurrentTime(audio.currentTime);
  }, [setCurrentTime]);

  const handleLoadedMetadata = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      setDuration(audio.duration);
      setLoading(false);
    }
  }, [setDuration, setLoading]);

  const handleLoadStart = useCallback(() => {
    setLoading(true);
  }, [setLoading]);

  const handleCanPlay = useCallback(() => {
    setLoading(false);
  }, [setLoading]);

  const handleEnded = useCallback(() => {
    trackEnded();
  }, [trackEnded]);

  const handleError = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const mediaError = audio.error;
    let msg = 'Failed to play audio';
    if (mediaError) {
      switch (mediaError.code) {
        case MediaError.MEDIA_ERR_ABORTED:
          msg = 'Playback aborted';
          break;
        case MediaError.MEDIA_ERR_NETWORK:
          msg = 'Network error while loading audio';
          break;
        case MediaError.MEDIA_ERR_DECODE:
          msg = 'Audio file could not be decoded — it may be corrupted';
          break;
        case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
          msg = 'Audio format not supported by your browser';
          break;
      }
    }
    setError(msg);
    setLoading(false);
  }, [setError, setLoading]);

  const handleProgress = useCallback(() => {
    const audio = audioRef.current;
    if (audio && audio.buffered.length > 0) {
      const bufferedEnd = audio.buffered.end(audio.buffered.length - 1);
      setBuffered((bufferedEnd / audio.duration) * 100);
    }
  }, [setBuffered]);

  // Attach/detach all event listeners in a single effect
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('loadstart', handleLoadStart);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);
    audio.addEventListener('progress', handleProgress);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('loadstart', handleLoadStart);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('progress', handleProgress);
    };
  }, [
    handleTimeUpdate,
    handleLoadedMetadata,
    handleLoadStart,
    handleCanPlay,
    handleEnded,
    handleError,
    handleProgress,
  ]);

  // This component renders nothing — it only manages the hidden <audio> element.
  // All audio playback is controlled through AudioPlayerContext.
  return null;
}

export default PersistentAudioElement;
